import type { ApprovalAttributes } from '@/types/approval';
import { localize } from '@/utils/approval/localize';
import {
  VEHICLE_COST_HR_MISSING_MESSAGE,
  type VehicleCostEmployee,
} from '@/utils/approval/vehicleCost';

/** 퇴직원 — 레거시 appr_edit_forms_resignation */
export const RESIGNATION_TYPE = 196;

export const isResignationType = (apvType: number): boolean =>
  apvType === RESIGNATION_TYPE;

/** 레거시처럼 언어와 무관하게 한국어 문구가 곧 저장값 */
export const RESIGNATION_REASONS = [
  '개인사정',
  '정년퇴직',
  '계약만료',
  '권고사직',
] as const;

export const RESIGNATION_PERSONAL_REASON = '개인사정';

export const RESIGNATION_REASON_DETAILS = [
  '이직',
  '개인사업',
  '육아',
  '휴식',
  '건강상의 사유(본인)',
  '거주지 변동(해외이주 등)',
  '학업',
  '직무변경',
  '기타',
] as const;

export const RESIGNATION_OTHER_DETAIL = '기타';

export const RESIGNATION_DETAIL_WRITE_MAX = 200;

export type ResignationPledge = 'Y' | 'N';

export type ResignationFormState = {
  employee: VehicleCostEmployee;
  lastWorkDate: string;
  retireDate: string;
  reason: string;
  reasonDetail: string;
  reasonDetailWrite: string;
  pledgeAgree: ResignationPledge | '';
};

/** 레거시 CL02 JSON 키 그대로 */
export type ResignationData = {
  emp_no: string;
  emp_name: string;
  emp_company: string;
  emp_gr_end_date: string;
  last_work_date: string;
  retire_date: string;
  reason: string;
  reason_detail?: string;
  reason_detail_write: string;
  pledge_agree: string;
};

export const createResignationState = (
  employee: VehicleCostEmployee,
): ResignationFormState => ({
  employee,
  lastWorkDate: '',
  retireDate: '',
  reason: '',
  reasonDetail: '',
  reasonDetailWrite: '',
  pledgeAgree: '',
});

export const buildResignationSubject = (
  name: string,
  retireDate?: string,
): string =>
  retireDate
    ? localize('퇴직원 ({{name}} / {{retireDate}} 퇴직)', { name, retireDate })
    : localize('퇴직원 ({{name}} 퇴직)', { name });

/** 레거시 입력 처리 — 제어 문자는 공백, 200자 초과분은 자름 */
export const sanitizeReasonDetailWrite = (value: string): string =>
  value
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ')
    .slice(0, RESIGNATION_DETAIL_WRITE_MAX);

/** 레거시 save_resignation 검사 순서·문구 그대로(임시저장도 같은 검사) */
export const checkResignationBeforeSave = (
  state: ResignationFormState | null | undefined,
): string | null => {
  if (!state) return VEHICLE_COST_HR_MISSING_MESSAGE;
  if (!state.lastWorkDate) return '마지막 근무일을 입력해주세요.';
  if (!state.retireDate) return '퇴직일을 입력해주세요.';
  if (!state.reason) return '퇴직사유를 선택해주세요.';
  if (state.reason === RESIGNATION_PERSONAL_REASON) {
    if (!state.reasonDetail) return '퇴직 상세사유를 선택해주세요.';
    if (
      state.reasonDetail === RESIGNATION_OTHER_DETAIL &&
      !state.reasonDetailWrite
    ) {
      return '퇴직 상세 사유를 작성해주세요.';
    }
  }
  if (state.pledgeAgree !== 'Y') {
    return '퇴직 서약서에 동의해야 품의 상신이 가능합니다.';
  }
  return null;
};

export const toResignationData = (
  state: ResignationFormState,
): ResignationData => ({
  emp_no: state.employee.employeeNumber,
  emp_name: state.employee.name,
  emp_company: state.employee.affiliation,
  emp_gr_end_date: state.employee.enterDate,
  last_work_date: state.lastWorkDate,
  retire_date: state.retireDate,
  reason: state.reason,
  // 레거시는 상세사유를 고르지 않으면 키 자체가 빠짐
  ...(state.reasonDetail ? { reason_detail: state.reasonDetail } : {}),
  reason_detail_write: sanitizeReasonDetailWrite(state.reasonDetailWrite),
  pledge_agree: state.pledgeAgree,
});

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** 단건조회 attributes.data. 레거시 문서는 JSON 문자열일 수 있음. emp_no 없으면 null */
export const parseResignationData = (
  attributes: unknown,
): ResignationData | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  let raw = (attributes as Record<string, unknown>).data;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (!str(d.emp_no)) return null;
  return {
    emp_no: str(d.emp_no),
    emp_name: str(d.emp_name),
    emp_company: str(d.emp_company),
    emp_gr_end_date: str(d.emp_gr_end_date),
    last_work_date: str(d.last_work_date),
    retire_date: str(d.retire_date),
    reason: str(d.reason),
    ...(str(d.reason_detail) ? { reason_detail: str(d.reason_detail) } : {}),
    reason_detail_write: str(d.reason_detail_write),
    pledge_agree: str(d.pledge_agree),
  };
};

export const parseResignationContent = (attributes: unknown): string | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const raw = (attributes as Record<string, unknown>).content;
  return typeof raw === 'string' && raw.trim() ? raw : null;
};

/** 편집 진입 — 저장 data 로 폼 복원(레거시는 복원하지 않음) */
export const resignationDataToState = (
  data: ResignationData,
): ResignationFormState => ({
  employee: {
    name: data.emp_name,
    employeeNumber: data.emp_no,
    affiliation: data.emp_company,
    enterDate: data.emp_gr_end_date,
  },
  lastWorkDate: data.last_work_date,
  retireDate: data.retire_date,
  reason: data.reason,
  reasonDetail: data.reason_detail ?? '',
  reasonDetailWrite: data.reason_detail_write,
  pledgeAgree:
    data.pledge_agree === 'Y' || data.pledge_agree === 'N'
      ? data.pledge_agree
      : '',
});

/** 저장 직전 — 직접 작성 칸을 레거시 입력 규칙으로 정리 */
export const normalizeResignationState = (
  state: ResignationFormState,
): ResignationFormState => ({
  ...state,
  reasonDetailWrite: sanitizeReasonDetailWrite(state.reasonDetailWrite),
});

export const buildResignationAttributes = (
  state: ResignationFormState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state ? { content, data: toResignationData(state) } : null;

/** 'yyyy-mm-dd' 를 로컬 자정으로 */
const parseYmd = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** 레거시 조회 calculateServicePeriod — 입사일~퇴직일 연·개월. 날짜가 없으면 null */
export const calculateServicePeriod = (
  enterDate: string,
  retireDate: string,
): { years: number; months: number } | null => {
  const start = parseYmd(enterDate);
  const end = parseYmd(retireDate);
  if (!start || !end) return null;
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  if (end.getDate() - start.getDate() < 0) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return { years, months };
};
